import React, { useMemo, useState } from 'react'
import useSWR from 'swr'
import { Link } from 'react-router-dom'
import {
  createSlug,
  deleteSlug,
  listSlugs,
  lookupSlug,
  promoteSlug,
  type SlugListResponse,
  type SlugRecord,
} from '../services/api'
import { slugify } from '../utils/slugify'
import { useDebounce, useCopyToClipboard } from '../hooks'
import { Badge } from './Badge'
import { Banner } from './Banner'
import { Button } from './Button'
import { Panel } from './Panel'
import formStyles from '../styles/forms.module.scss'

export type SlugManagerEntityType =
  | 'player'
  | 'competition_parent'
  | 'match'
  | 'team'
  | 'article'

interface SlugManagerProps {
  entityType: SlugManagerEntityType
  entityId: string
  /** When true, render without the outer Panel/h2 so it fits inside a modal
   *  or other form section. Uses an h3 to match sibling sections. */
  embedded?: boolean
}

type LookupState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'available'; slug: string }
  | { kind: 'taken'; owner: SlugRecord }

const ENTITY_LABELS: Record<SlugManagerEntityType, string> = {
  player: 'Player',
  competition_parent: 'Tournament',
  match: 'Match',
  team: 'Team',
  article: 'Article',
}

function ownerLink(owner: SlugRecord): string | null {
  switch (owner.entity_type) {
    case 'player':
      return `/players/${owner.entity_id}`
    case 'competition_parent':
      return `/tournaments/${owner.entity_id}`
    case 'match':
      return `/matches/${owner.entity_id}`
    case 'team':
      return `/teams/${owner.entity_id}`
    case 'article':
      return `/content/articles/${owner.entity_id}`
    default:
      return null
  }
}

function isAutoGenerated(createdBy: string): boolean {
  return createdBy === 'system' || createdBy === 'system-backfill'
}

// Shared grid: slug | badge (auto/manual + copy) | promote | delete.
const slugGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content max-content max-content max-content',
  columnGap: '8px',
  rowGap: '6px',
  alignItems: 'center',
}

const slugGridLabelStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--tc-text-secondary, #6b7280)',
  marginTop: '4px',
}

interface SlugRowCellsProps {
  row: SlugRecord
  isMutating: boolean
  copiedId: string | null
  onCopy: (id: string, slug: string) => void
  onPromote?: (id: string) => void
  onDelete?: (id: string) => void
}

const SlugRowCells: React.FC<SlugRowCellsProps> = ({
  row,
  isMutating,
  copiedId,
  onCopy,
  onPromote,
  onDelete,
}) => {
  const auto = isAutoGenerated(row.created_by)
  const justCopied = copiedId === row.tc_slug_id
  return (
    <>
      <code className="id-code">{row.slug}</code>
      <Badge
        variant={auto ? 'neutral' : 'secondary'}
        clickable
        onClick={() => onCopy(row.tc_slug_id, row.slug)}
        icon={justCopied ? 'check' : 'clipboard'}
        ariaLabel={`Copy slug ${row.slug}`}
      >
        {auto ? 'system' : 'manual'}
      </Badge>
      {onPromote ? (
        <Button
          variant="secondary"
          size="small"
          disabled={isMutating}
          onClick={() => onPromote(row.tc_slug_id)}
        >
          Make canonical
        </Button>
      ) : (
        <span />
      )}
      {onDelete ? (
        <Button
          variant="danger"
          size="small"
          disabled={isMutating}
          onClick={() => onDelete(row.tc_slug_id)}
        >
          Delete
        </Button>
      ) : (
        <span />
      )}
    </>
  )
}

export const SlugManager: React.FC<SlugManagerProps> = ({ entityType, entityId, embedded = false }) => {
  const { data, isLoading, error, mutate } = useSWR<SlugListResponse>(
    entityId ? ['slugs', entityType, entityId] : null,
    () => listSlugs(entityType, entityId),
    { revalidateOnFocus: false }
  )

  const [rawInput, setRawInput] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [isMutating, setIsMutating] = useState(false)
  const { copiedId, handleCopy } = useCopyToClipboard()

  const preview = useMemo(() => slugify(rawInput), [rawInput])
  const debouncedPreview = useDebounce(preview, 300)

  const ownSlugIds = useMemo(() => {
    const ids = new Set<string>()
    if (data?.canonical) ids.add(data.canonical.tc_slug_id)
    for (const r of data?.redirects ?? []) ids.add(r.tc_slug_id)
    return ids
  }, [data])

  const { data: lookupData, isValidating: lookupValidating } = useSWR(
    debouncedPreview ? ['slug-lookup', debouncedPreview] : null,
    async ([, slug]) => {
      // Any error here (404, network) is treated as "slug is free / unknown".
      // The server still enforces correctness on the create call.
      try {
        return await lookupSlug(slug)
      } catch {
        return null
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false }
  )

  const lookupState: LookupState = !debouncedPreview
    ? { kind: 'idle' }
    : preview !== debouncedPreview || (lookupValidating && lookupData === undefined)
      ? { kind: 'loading' }
      : lookupData
        ? { kind: 'taken', owner: lookupData }
        : { kind: 'available', slug: debouncedPreview }

  const ownsLookupHit =
    lookupState.kind === 'taken' && ownSlugIds.has(lookupState.owner.tc_slug_id)

  const canSubmit =
    !isMutating &&
    preview.length > 0 &&
    lookupState.kind === 'available'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setActionError(null)
    setIsMutating(true)
    try {
      await createSlug({
        entity_type: entityType,
        entity_id: entityId,
        slug: preview,
        is_canonical: true,
      })
      setRawInput('')
      await mutate()
    } catch (err) {
      setActionError((err as Error)?.message || 'Failed to create slug')
    } finally {
      setIsMutating(false)
    }
  }

  async function handlePromote(tcSlugId: string) {
    setActionError(null)
    setIsMutating(true)
    try {
      await promoteSlug(tcSlugId)
      await mutate()
    } catch (err) {
      setActionError((err as Error)?.message || 'Failed to promote slug')
    } finally {
      setIsMutating(false)
    }
  }

  async function handleDelete(tcSlugId: string) {
    setActionError(null)
    setIsMutating(true)
    try {
      await deleteSlug(tcSlugId)
      await mutate()
    } catch (err) {
      setActionError((err as Error)?.message || 'Failed to delete slug')
    } finally {
      setIsMutating(false)
    }
  }

  const canonical = data?.canonical ?? null
  const redirects = data?.redirects ?? []

  const statusNode = (() => {
    if (!preview) return null
    switch (lookupState.kind) {
      case 'loading':
        return <span>Checking…</span>
      case 'available':
        return <span>Available.</span>
      case 'taken':
        if (ownsLookupHit) {
          return (
            <span>
              Already attached to this {ENTITY_LABELS[entityType].toLowerCase()}.
            </span>
          )
        }
        return (
          <span>
            In use by{' '}
            {ownerLink(lookupState.owner) ? (
              <Link to={ownerLink(lookupState.owner) as string}>
                {lookupState.owner.owner_name || lookupState.owner.entity_id}
              </Link>
            ) : (
              <span>{lookupState.owner.owner_name || lookupState.owner.entity_id}</span>
            )}{' '}
            ({ENTITY_LABELS[lookupState.owner.entity_type as SlugManagerEntityType] ?? lookupState.owner.entity_type}
            {lookupState.owner.is_canonical ? ', canonical' : ', redirect'})
          </span>
        )
      default:
        return null
    }
  })()

  const body = (
    <>
      {error && (
        <Banner
          variant="error"
          message={`Failed to load slugs: ${(error as Error).message}`}
          onDismiss={() => mutate()}
        />
      )}
      {actionError && (
        <Banner
          variant="error"
          message={actionError}
          onDismiss={() => setActionError(null)}
          autoScroll
        />
      )}

      {isLoading ? (
        <p className={formStyles.hint}>Loading slugs…</p>
      ) : (
        <div className={formStyles.formField}>
          <div style={slugGridStyle}>
            <div style={{ ...slugGridLabelStyle, marginTop: 0 }}>Canonical</div>
            {canonical ? (
              <SlugRowCells
                row={canonical}
                isMutating={isMutating}
                copiedId={copiedId}
                onCopy={handleCopy}
              />
            ) : (
              <p
                className={formStyles.hint}
                style={{ gridColumn: '1 / -1', margin: 0 }}
              >
                No canonical slug yet.
              </p>
            )}

            {redirects.length > 0 && (
              <>
                <div style={slugGridLabelStyle}>Redirects</div>
                {redirects.map(r => (
                  <SlugRowCells
                    key={r.tc_slug_id}
                    row={r}
                    isMutating={isMutating}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                    onPromote={handlePromote}
                    onDelete={handleDelete}
                  />
                ))}
              </>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className={formStyles.fieldGroup}>
              <label>Add a custom slug</label>
              <div className={formStyles.formActions}>
                <input
                  type="text"
                  value={rawInput}
                  onChange={e => setRawInput(e.target.value)}
                  placeholder="Type a name or slug — normalized as you type"
                  disabled={isMutating}
                  style={{ flex: 1 }}
                />
                <Button type="submit" disabled={!canSubmit}>
                  {canonical ? 'Set as canonical' : 'Create canonical slug'}
                </Button>
              </div>
              <p className={formStyles.hint}>
                {preview ? (
                  <>
                    Preview: <code className="id-code">{preview}</code>
                    {statusNode && <> — {statusNode}</>}
                  </>
                ) : (
                  <>&nbsp;</>
                )}
              </p>
            </div>
          </form>
        </div>
      )}
    </>
  )

  if (embedded) {
    return (
      <>
        <h3>URL Slug</h3>
        {body}
      </>
    )
  }

  return (
    <Panel>
      <Panel.Header>
        <h2>URL Slug</h2>
      </Panel.Header>
      {body}
    </Panel>
  )
}

export default SlugManager
